import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import type { ScholarlyArticle } from "../../types/formTypes";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const ArticleList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ArticleFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface ScholarlyArticleFieldProps {
  label: string;
  name: string;
  value: ScholarlyArticle[];
  onChange: (value: ScholarlyArticle[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Publication" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Publication" */
  addButtonLabel?: string;
}

const emptyArticle: ScholarlyArticle = {
  identifier: "",
};

const ScholarlyArticleField: React.FC<ScholarlyArticleFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Publication",
  addButtonLabel = "+ Add Publication",
}) => {
  const initialItems =
    value.length > 0 ? value : required ? [emptyArticle] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const articles = value.length > 0 ? value : required ? [emptyArticle] : [];

  const updateArticle = (index: number, updates: Partial<ScholarlyArticle>) => {
    const next = [...articles];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addArticle = () => {
    const next = [...articles, { ...emptyArticle }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeArticle = (index: number) => {
    if (articles.length <= 1 && required) return;
    const next = articles.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const collapseItems = articles.map((article, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(articles.length > 1 || !required) && articles.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeArticle(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <ArticleFieldsWrapper>
        <TextField
          label="Identifier"
          name={`${name}-${index}-identifier`}
          value={article.identifier ?? ""}
          onChange={(v) => updateArticle(index, { identifier: v })}
          required
          tooltip="URL identifier, ideally a DOI for a publication."
        />
      </ArticleFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <ArticleList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={collapseItems}
        />
        <AddButton type="button" onClick={addArticle}>
          {addButtonLabel}
        </AddButton>
      </ArticleList>
    </FieldWrapper>
  );
};

export default ScholarlyArticleField;
